import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useDeleteMembership } from 'src/features/membership/api/useDeleteMembership';
import { LIST_MEMBERSHIPS_RESOURCE } from 'src/features/membership/api/useListMemberships';
import { Membership } from 'src/features/membership/types';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface MembershipFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMembership: Membership;
    children?: React.ReactNode;
}

function DeleteMembershipFormModal<TData>({
    selectedMembership,
    semantics,
    children,
}: MembershipFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const navigate = useNavigate();
    const { parkingId } = useParking();

    const handleNavigationAfterDeletion = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate(`/${PATH.MEMBERSHIPS}`);
        }
    };

    const { mutate: deleteMembership, isPending: isPendingDelete } = useDeleteMembership({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIPS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(membershipMessages.deleteMembershipSuccess),
                description: formatMessage(membershipMessages.deleteMembershipSuccessDescription, {
                    entry: selectedMembership?.name,
                }),
            });
            handleNavigationAfterDeletion();
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => deleteMembership({ parkingId, membershipId: selectedMembership.id });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader className="flex flex-row gap-2">
                    <SquareIcon icon={ICON_MAP.DELETE} />
                    <div>
                        <DialogTitle>
                            <FormattedMessage
                                {...generalMessages.deleteEntry}
                                values={{ entry: semantics?.entryName?.toLowerCase() }}
                            />
                        </DialogTitle>
                        <DialogDescription>
                            <FormattedMessage
                                {...membershipMessages.deleteMembershipSubtitle}
                                values={{ entry: selectedMembership.name }}
                            />
                        </DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            {...membershipMessages.confirmDeleteMembership}
                            values={{ entry: selectedMembership?.name }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPendingDelete}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingDelete && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage
                            {...generalMessages.deleteEntry}
                            values={{ entry: semantics?.entryName?.toLowerCase() }}
                        />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeleteMembershipFormModal;
